// https://material-ui.com/api/text-field/

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { compose } from 'redux';
import withGuid from "../../hocs/with_guid.js";
import withHelperText from "../../hocs/with_helper_text.js";

import { TextField as MaterialTextField, FormLabel, IconButton } from '@material-ui/core';

import IcOpenedEye from '../../resources/icons/ic_opened_eye.js';
import IcClosedEye from '../../resources/icons/ic_closed_eye.js';

import { 
  InputVariantStandard, InputVariantOutlined, InputVariantFilled,
  InputLabelDisplayModeInside, InputLabelDisplayModeAbove
} from "./constants.js.jsx";

const styles = theme => ({
  container: {
    position: "relative"
  },
  root: {
  },
  withEye: {
    "& .MuiInputBase-input": {
      paddingRight: "54px"
    }
  },
  label: {
    display: "block",
  },
  eye: {
    position: "absolute",
    top: "13%",
    right: "12px"
  }
});

/**
 * Callback for inputs
 * @callback PasswordField~onChange
 * @param {string} value input's new value
 */

/**
 * Component for input password. Already includes HOCs {@link WithGuid}, {@link WithHelperText}
 * @name PasswordField
 * @param {object} props React component properties
 * @param {any} [props.value] Field's value
 * @param {string} [props.displayName] Field's display name
 * @param {boolean} [props.hasErrors = false] Does errors exists on the field?
 * @param {boolean} [props.disableShowPassword = false] If it equals true, field will hide eye button
 * @param {Array.<string>} [props.errors = []] Error messages array
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @param {string} [props.className] CSS-class of button
 * @param {string} [props.variant = "outlined"] View variant for text field stylization. Available values:
 *  - "standard"
 *  - "outlined"
 *  - "filled"
 * 
 * Explanation of variants: {@link https://material-ui.com/components/text-fields/#textfield}
 * 
 * NOTE: for providing variant, please, import necessary variant as constant from file "components/base/inputs/constants.js.jsx"
 * @param {string} [props.displayNamePosition = "inside"] Position of field's display name: inside the field with floating style or above. Available values:
 *  - "inside"
 *  - "above"
 * 
 * NOTE: for providing position, please, import necessary position as constant from file "components/base/inputs/constants.js.jsx"
 * @param {PasswordField~onChange} [props.onChange] On change handler
 * @example
 * return (
 *   <PasswordField className={ "awesome-class" } 
 *                  onChange = { (value) => {
 *                    // Some actions
 *                  } } 
 *                  value = { "password" } />
 * )
*/

class PasswordField extends React.Component {
  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onEyeClicked = this.onEyeClicked.bind(this);

    this.state = {
      passwordShowing: false
    }
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    let propsValue   = `${props.value || ""}`;
    let defaultValue = `${state.defaultValue || ""}`;
    let stateValue   = `${state.value || ""}`;
    
    if(defaultValue !== propsValue) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      value = stateValue;
    }

    return {
      value: value,
      defaultValue: defaultValue,
      disableShowPassword: props.disableShowPassword
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.value !== nextState.value ||
           this.props.displayName !== nextProps.displayName ||
           this.state.passwordShowing !== nextState.passwordShowing ||
           this.props.hasError !== nextProps.hasError ||
           this.props.readOnly !== nextProps.readOnly ||
           this.state.disableShowPassword !== nextState.disableShowPassword;
  }

  onEyeClicked() {
    const { passwordShowing } = this.state;
    this.setState({
      passwordShowing: !passwordShowing
    })
  }

  onChange(event) {
    const { onChange } = this.props;
    let value = event.target.value;
    this.setState({
      value: value
    });

    if(onChange) {
      onChange(value);
    }
  }

  render() {
    const { classes, className, guid, hasError, readOnly } = this.props;

    const { name, displayName, variant, underline, displayNamePosition } = this.props;

    const { value, passwordShowing, disableShowPassword } = this.state;

    let inputProps = {};
    if(variant === InputVariantFilled) {
      inputProps.disableUnderline = !underline;
    }

    return <React.Fragment>
      {
        displayNamePosition === InputLabelDisplayModeAbove &&
        <FormLabel htmlFor = { guid }
                   error = { hasError } >
          { `${displayName}:` }
        </FormLabel>
      }
      <div className = { classes.container } >
        <MaterialTextField className = { clsx({
                              [classes.root]: true,
                              [className]: true,
                              [classes.withEye]: !disableShowPassword
                           }) } 
                           id = { guid } 
                           fullWidth
                           error = { hasError }
                           InputProps = { inputProps }
                           type = { passwordShowing ? "text" : "password" }
                           name = { name }
                           label = { displayNamePosition === InputLabelDisplayModeInside ? displayName : undefined }
                           variant = { variant }
                           value = { value ? value : "" } 
                           onChange = { this.onChange }
                           disabled = { readOnly } />
        <IconButton className = { classes.eye } 
                    onClick = { this.onEyeClicked }>
          { !disableShowPassword && passwordShowing  && <IcOpenedEye /> }
          { !disableShowPassword && !passwordShowing && <IcClosedEye /> }
        </IconButton>
      </div>
    </React.Fragment>
  }
}

PasswordField.propTypes = {
  variant: PropTypes.oneOf([InputVariantStandard, InputVariantOutlined, InputVariantFilled]),
  displayNamePosition: PropTypes.oneOf([InputLabelDisplayModeAbove, InputLabelDisplayModeInside]),
  readOnly: PropTypes.bool,
  disableShowPassword: PropTypes.bool
};

PasswordField.defaultProps = {
  variant: InputVariantOutlined,
  displayNamePosition: InputLabelDisplayModeInside,
  underline: true,
  readOnly: false,
  disableShowPassword: false
}

export default compose(withGuid, withHelperText, withStyles(styles))(PasswordField)
